import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";

const systemPillars = [
  {
    title: "Governance",
    description: "Authority infrastructure that operates without founder dependency.",
    href: "/governance",
  },
  {
    title: "Certification",
    description: "Structured progression through doctrine that determines license eligibility.",
    href: "/certification",
  },
  {
    title: "Enforcement",
    description: "Revocable access, audit compliance, and violation management.",
    href: "/systems",
  },
];

const proofStatements = [
  "Built for transfer.",
  "Designed for audit.",
  "Structured for exit.",
];

const audienceGates = [
  { name: "Operators", href: "/operator-application", description: "Apply for certification" },
  { name: "Investors", href: "/investor-access", description: "View metrics and documentation" },
  { name: "Boards", href: "/investor-access", description: "Governance and oversight" },
];

const HomePage = () => {
  return (
    <div className="min-h-screen bg-[#080808]" data-testid="home-page">
      <SEOHead />
      <Navbar />

      {/* Hero Section - Full Height */}
      <section className="min-h-screen flex items-center justify-center px-6 md:px-12" data-testid="hero-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[1.1] text-foreground mb-8">
              Authority Is Engineered.<br />
              <span className="text-primary">Not Marketed.</span>
            </h1>
            <p className="text-lg md:text-xl font-light text-muted-foreground mb-12 max-w-2xl mx-auto">
              The operating system behind founder-independent businesses.
            </p>
            <Link 
              to="/systems" 
              className="inline-flex items-center text-sm text-primary hover:text-primary/80 transition-colors group"
              data-testid="view-system-link"
            >
              View the System
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* System Pillars - 3 Columns */}
      <section className="py-24 md:py-32 px-6 md:px-12 border-t border-white/5" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5">
            {systemPillars.map((pillar, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link 
                  to={pillar.href}
                  className="block bg-[#080808] p-10 md:p-12 hover:bg-[#0a0a0a] transition-colors h-full group"
                  data-testid={`pillar-${pillar.title.toLowerCase()}`}
                >
                  <h2 className="font-serif text-2xl md:text-3xl font-light text-foreground mb-4 group-hover:text-primary transition-colors">
                    {pillar.title}
                  </h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {pillar.description}
                  </p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Proof Without Logos */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#050505]" data-testid="proof-section">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            {proofStatements.map((statement, index) => (
              <motion.p
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="font-serif text-2xl md:text-3xl lg:text-4xl font-light text-foreground"
              >
                {statement}
              </motion.p>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Audience Gate */}
      <section className="py-24 md:py-32 px-6 md:px-12 border-t border-white/5" data-testid="audience-section">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground">
              Select Your Path
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {audienceGates.map((gate, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <Link 
                  to={gate.href}
                  className="group block"
                  data-testid={`gate-${gate.name.toLowerCase()}`}
                >
                  <h3 className="font-serif text-2xl md:text-3xl font-light text-foreground mb-3 group-hover:text-primary transition-colors">
                    {gate.name}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {gate.description}
                  </p>
                  <div className="mt-4 flex justify-center">
                    <ArrowRight className="h-4 w-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Book - Authority Architecture */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#050505]" data-testid="book-section">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-card/5 border border-white/10 p-12 md:p-16"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              The Doctrine
            </p>
            <h2 className="font-serif text-3xl md:text-4xl font-light text-foreground mb-4">
              The Authority Operating Manual
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              The written architecture behind OnPoint Authority Systems.
            </p>
            <Link 
              to="/book" 
              className="inline-flex items-center text-sm text-primary hover:text-primary/80 transition-colors group"
              data-testid="view-book-link"
            >
              View the Book
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Closing Statement */}
      <section className="py-24 md:py-32 px-6 md:px-12" data-testid="closing-section">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="font-serif text-xl md:text-2xl font-light text-muted-foreground leading-relaxed">
              This is not branding. This is not consulting.<br />
              <span className="text-foreground">This is enterprise authority engineering.</span>
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
